import { useCallback, useEffect, useState } from "react";
import { ChevronRight, Plus } from "lucide-react";
import { api, type Account, type Folder, type SetupState } from "../api";
import { bestaetigen, fehlerMelden, fehlerText, toast } from "./verwaltung";
import { disablePush, enablePush, erlaubnisHolen, hinweisStand, pushState, type HinweisStand, type PushState } from "../lib/notifications";
import { ModelAccess } from "./ModelAccess";
import { PrivacyPanel } from "./PrivacySettings";
import { ReindexPanel } from "./Reindex";
import { AbwesenheitPanel } from "./Abwesenheit";

const PROVIDER_NAMES: Record<string, string> = {
  langdock: "Langdock (EU)",
  openai: "OpenAI direkt (USA)",
  azure: "Azure OpenAI",
  mistral: "Mistral AI (Frankreich)",
  "openai-kompatibel": "Eigener Endpunkt",
  anthropic: "Anthropic direkt (USA)",
};
const providerName = (id: string) => PROVIDER_NAMES[id] ?? id;

/** Modellzugang, Benachrichtigungen, Postfächer und Signaturen an einem Ort. */
type Bereich = "postfaecher" | "modell" | "datenschutz" | "abwesenheit" | "benachrichtigungen" | "pflege";

const BEREICHE: { id: Bereich; label: string }[] = [
  { id: "postfaecher", label: "Postfächer" },
  { id: "modell", label: "Modell" },
  { id: "datenschutz", label: "Datenschutz" },
  { id: "abwesenheit", label: "Abwesenheit" },
  { id: "benachrichtigungen", label: "Benachrichtigungen" },
  { id: "pflege", label: "Neu einordnen" },
];

export function SettingsPage({ onOpenWizard, folders }: { onOpenWizard: () => void; folders: Folder[] }) {
  const [accounts, setAccounts] = useState<Account[] | null>(null);
  const [setup, setSetup] = useState<SetupState | null>(null);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [modelOpen, setModelOpen] = useState(false);
  const [bereich, setBereich] = useState<Bereich>("postfaecher");

  const load = useCallback(() => {
    api.accounts().then((a) => { setAccounts(a); setLadeFehler(null); }).catch((e) => setLadeFehler(fehlerText(e)));
    api.setupState().then(setSetup).catch(() => {});
  }, []);
  useEffect(load, [load]);

  // Ein fehlender Modellzugang ist das Einzige, was von hier aus wirklich blockiert.
  const modellFehlt = setup ? !setup.llm.configured : false;

  return (
    <section className="pane wide" data-active="true">
      <header className="pane-head">
        <span className="pane-title">Einstellungen</span>
      </header>

      {/* Eigene Zeile statt in der Kopfleiste: sechs Bereiche passen dort nicht
          nebeneinander, und am Telefon soll die Liste rollen können. */}
      <nav className="subnav" aria-label="Bereiche der Einstellungen">
        {BEREICHE.map((b) => (
          <button key={b.id} className="subnav-tab" aria-current={bereich === b.id ? "page" : undefined}
            data-aktiv={bereich === b.id} onClick={() => { setBereich(b.id); setModelOpen(false); }}>
            {b.label}
            {b.id === "modell" && modellFehlt && <span className="subnav-punkt" aria-label="nicht eingerichtet" />}
          </button>
        ))}
      </nav>

      <div className="pane-scroll">
        <div className="page v-bereich">
          {bereich === "postfaecher" && (
            <>
              <div className="v-kopf">
                <div>
                  <h2>Postfächer</h2>
                  <p className="lede">
                    Name und Signatur je Konto. Die Signatur hängt der Agent an jeden Antwortentwurf an — schreib sie
                    so, wie du sie wirklich verwendest.
                  </p>
                </div>
                <div className="v-kopf-aktionen">
                  <button className="btn primary" onClick={onOpenWizard}><Plus aria-hidden="true" /> Postfach hinzufügen</button>
                </div>
              </div>

              {ladeFehler && !accounts ? (
                <div className="v-leer" data-art="fehler">
                  <strong>Die Postfächer ließen sich nicht laden</strong>
                  <span>{ladeFehler}</span>
                  <button className="btn" onClick={load}>Erneut versuchen</button>
                </div>
              ) : !accounts ? (
                <div className="v-laden" role="status">Lade Postfächer …</div>
              ) : accounts.length === 0 ? (
                <div className="v-leer">
                  <strong>Noch kein Postfach verbunden</strong>
                  <span>Über „Postfach hinzufügen" führt dich der Assistent durch die Verbindung.</span>
                </div>
              ) : (
                accounts.map((a) => <AccountCard key={a.id} account={a} einziges={accounts.length === 1} onSaved={load} />)
              )}
            </>
          )}

          {bereich === "modell" && (
            <>
              {setup && modelOpen ? (
                <div className="v-panel">
                  <div className="v-panel-rumpf">
                    <ModelAccess current={setup.llm} onSaved={() => { setModelOpen(false); toast.erfolg("Modellzugang gespeichert. Gilt ab sofort."); load(); }}
                      onCancel={() => setModelOpen(false)} />
                  </div>
                </div>
              ) : (
                <>
                  <div className="v-kopf">
                    <div>
                      <h2>Modellzugang</h2>
                      <p className="lede">
                        Welcher Anbieter die Mails einordnet und wo das verarbeitet wird. Ein Wechsel gilt sofort, ohne Neustart.
                      </p>
                    </div>
                  </div>
                  {!setup ? (
                    <div className="v-laden" role="status">Lade …</div>
                  ) : (
                    <div className="rule">
                      <div className="rule-head" style={{ cursor: "default" }}>
                        <span className="state-dot" data-state={setup.llm.configured ? "da" : "ueberfaellig"} aria-hidden="true" />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div className="rule-name">
                            {setup.llm.configured ? providerName(setup.llm.provider) : "Noch kein Zugang hinterlegt"}
                          </div>
                          <div className="rule-summary">
                            {setup.llm.configured
                              ? `${setup.llm.model}${setup.llm.fastModel ? ` · zum Sortieren: ${setup.llm.fastModel}` : ""}`
                              : "Ohne Zugang werden Mails abgeholt und auf Spam geprüft, aber nicht eingeordnet."}
                          </div>
                          {setup.llm.baseUrl && <div className="rule-summary mono">{setup.llm.baseUrl}</div>}
                        </div>
                        <button className={setup.llm.configured ? "btn" : "btn primary"} onClick={() => setModelOpen(true)}>
                          {setup.llm.configured ? "Ändern" : "Einrichten"}
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {bereich === "datenschutz" && <PrivacyPanel folders={folders} />}
          {bereich === "abwesenheit" && <AbwesenheitPanel accounts={accounts ?? []} />}
          {bereich === "benachrichtigungen" && <NotificationSettings />}
          {bereich === "pflege" && <ReindexPanel folders={folders} onZumModell={() => setBereich("modell")} />}
        </div>
      </div>
    </section>
  );
}

function NotificationSettings() {
  const [state, setState] = useState<PushState>("aus");
  const [erlaubnis, setErlaubnis] = useState<HinweisStand>("offen");
  const [busy, setBusy] = useState(false);

  const refresh = async () => {
    setState(await pushState());
    setErlaubnis(hinweisStand());
  };
  useEffect(() => { void refresh(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      if (state === "an") {
        await disablePush();
        toast.erfolg("Push-Benachrichtigungen auf diesem Gerät abgeschaltet.");
      } else {
        const result = await enablePush();
        if (result.ok) toast.erfolg("Push-Benachrichtigungen sind auf diesem Gerät aktiv.");
        else toast.fehler(result.grund ?? "Einschalten hat nicht geklappt.");
      }
      await refresh();
    } catch (e) {
      fehlerMelden(e);
    } finally {
      setBusy(false);
    }
  };

  const testen = async () => {
    try {
      const r = await api.pushTest();
      if (r.sent > 0) toast.erfolg(`Testmeldung an ${r.sent} ${r.sent === 1 ? "Gerät" : "Geräte"} geschickt.`);
      else toast.info("Kein Gerät angemeldet — erst Push einschalten.");
    } catch (e) {
      fehlerMelden(e, "Testmeldung fehlgeschlagen");
    }
  };

  return (
    <>
      <div className="v-kopf">
        <div>
          <h2>Benachrichtigungen</h2>
          <p className="lede">
            Bei neuen Mails meldet sich der Agent. Spam und Newsletter lösen bewusst nichts aus, und mehrere
            gleichzeitig eintreffende Mails kommen als eine Meldung.
          </p>
        </div>
      </div>

      {/* Zwei Stufen: Die Anzeigeerlaubnis reicht bei offener Seite, Push
          erreicht auch geschlossene Tabs. Getrennt, weil die erste Bitte klein
          ist und die zweite ein Abonnement nach sich zieht. */}
      <div className="v-panel">
        <div className="v-panel-rumpf v-reihe" style={{ alignItems: "flex-start", gap: 12 }}>
          <span className="state-dot" style={{ marginTop: 6 }} aria-hidden="true"
            data-state={erlaubnis === "erlaubt" ? "da" : erlaubnis === "verweigert" ? "ueberfaellig" : "fehlt"} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong>
              {erlaubnis === "erlaubt" ? "Hinweise bei offener Seite: erlaubt"
                : erlaubnis === "verweigert" ? "Hinweise bei offener Seite: vom Browser blockiert"
                : erlaubnis === "nicht-unterstuetzt" ? "Hinweise: von diesem Browser nicht unterstützt"
                : "Hinweise bei offener Seite: noch nicht erlaubt"}
            </strong>
            <p className="hint">
              Zeigt neue Mails an, solange die Seite offen ist — auch wenn das Fenster im Hintergrund liegt.
              {erlaubnis === "verweigert" && " Freigeben lässt sich das nur in den Browser-Einstellungen für diese Seite."}
            </p>
          </div>
          {erlaubnis !== "erlaubt" && (
            <button
              className="btn"
              disabled={busy || erlaubnis === "verweigert" || erlaubnis === "nicht-unterstuetzt"}
              onClick={async () => { await erlaubnisHolen(); await refresh(); }}
            >
              Erlauben
            </button>
          )}
        </div>
      </div>

      <div className="v-panel">
        <div className="v-panel-rumpf v-reihe" style={{ alignItems: "flex-start", gap: 12 }}>
          <span className="state-dot" style={{ marginTop: 6 }} aria-hidden="true"
            data-state={state === "an" ? "da" : state === "verweigert" ? "ueberfaellig" : "fehlt"} />
          <div style={{ flex: 1, minWidth: 200 }}>
            <strong>
              {state === "an" ? "Push auf diesem Gerät: an"
                : state === "verweigert" ? "Push: vom Browser blockiert"
                : state === "nicht-unterstuetzt" ? "Push: von diesem Browser nicht unterstützt"
                : "Push auf diesem Gerät: aus"}
            </strong>
            <p className="hint">
              Erreicht dich auch bei geschlossenem Tab. Gilt je Gerät und Browser; auf dem iPhone muss die Seite
              dafür zum Home-Bildschirm hinzugefügt sein.
            </p>
          </div>
          <div className="v-reihe">
            {state === "an" && <button className="btn quiet" onClick={() => void testen()}>Test senden</button>}
            <button className={state === "an" ? "btn" : "btn primary"} disabled={busy || state === "nicht-unterstuetzt" || state === "verweigert"}
              onClick={() => void toggle()}>
              {busy ? "Einen Moment …" : state === "an" ? "Abschalten" : "Einschalten"}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

function AccountCard({ account, einziges, onSaved }: { account: Account; einziges: boolean; onSaved: () => void }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(account.name);
  const [signature, setSignature] = useState("");
  const [geladen, setGeladen] = useState<"nein" | "ja" | "fehler">("nein");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || geladen === "ja") return;
    api.account(account.id).then((full) => {
      setSignature(full.signature ?? "");
      setName(full.name);
      setGeladen("ja");
    }).catch(() => setGeladen("fehler"));
  }, [open, geladen, account.id]);

  const save = async () => {
    setBusy(true);
    try {
      await api.updateAccount(account.id, { name: name.trim(), signature: signature.trim() ? signature : null });
      toast.erfolg(`${account.email}: gespeichert.`);
      onSaved();
    } catch (e) {
      fehlerMelden(e, "Speichern fehlgeschlagen");
    } finally {
      setBusy(false);
    }
  };

  const aktivSchalten = async () => {
    if (account.active) {
      const ja = await bestaetigen({
        titel: `${account.email} pausieren?`,
        text: "Der Agent holt und sortiert keine neuen Mails mehr aus diesem Postfach, bis du es wieder aktivierst.",
        bestaetigen: "Pausieren",
      });
      if (!ja) return;
    }
    try {
      await api.updateAccount(account.id, { active: !account.active });
      toast.erfolg(account.active ? `${account.email} pausiert.` : `${account.email} ist wieder aktiv.`);
      onSaved();
    } catch (e) {
      fehlerMelden(e);
    }
  };

  const entfernen = async () => {
    const ja = await bestaetigen({
      titel: `${account.email} entfernen?`,
      text: <>Zugangsdaten, Ordner und die hier gespeicherten Kopien der Mails werden gelöscht. Auf dem Mailserver bleibt alles unverändert.{einziges ? " Danach ist kein Postfach mehr verbunden." : ""}</>,
      bestaetigen: "Entfernen",
      gefahr: true,
    });
    if (!ja) return;
    try {
      await api.deleteAccount(account.id);
      toast.erfolg(`${account.email} entfernt.`);
      onSaved();
    } catch (e) {
      fehlerMelden(e, "Entfernen fehlgeschlagen");
    }
  };

  return (
    <div className="rule" data-disabled={!account.active}>
      <div
        className="rule-head"
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => { if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); setOpen((v) => !v); } }}
      >
        <ChevronRight className="v-aufklapp" data-offen={open} aria-hidden="true" />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="rule-name">{account.email}</div>
          <div className="rule-summary">
            {account.name}
            {account.hasSmtp ? " · Versand eingerichtet" : " · kein Postausgang, Versand nicht möglich"}
          </div>
        </div>
        <span className="v-marke" data-an={account.active}>{account.active ? "aktiv" : "pausiert"}</span>
      </div>

      {open && (
        <div className="rule-body">
          {geladen === "nein" ? (
            <div className="v-laden" data-klein="true" role="status">Lade Signatur …</div>
          ) : geladen === "fehler" ? (
            <div className="v-meldung" data-art="fehler" role="alert">
              <span>Die Kontodaten ließen sich nicht laden. Damit die Signatur nicht versehentlich überschrieben wird, ist Speichern gesperrt.</span>
              <button className="btn quiet" onClick={() => setGeladen("nein")}>Erneut laden</button>
            </div>
          ) : (
            <form onSubmit={(e) => { e.preventDefault(); void save(); }}>
              <div className="field" style={{ marginTop: 4 }}>
                <label htmlFor={`k-name-${account.id}`}>Anzeigename</label>
                <input id={`k-name-${account.id}`} className="input" value={name} onChange={(e) => setName(e.target.value)} />
                <p className="hint">So erscheinst du beim Empfänger als Absender.</p>
              </div>

              <div className="field">
                <label htmlFor={`k-sig-${account.id}`}>Signatur</label>
                <textarea
                  id={`k-sig-${account.id}`}
                  className="textarea"
                  style={{ minHeight: 140 }}
                  value={signature}
                  placeholder={"Viele Grüße\nManu Wolke\n\nKreativ Wolke · kreativ-wolke.de"}
                  onChange={(e) => setSignature(e.target.value)}
                />
                <p className="hint">
                  Kommt automatisch unter jeden Antwortentwurf dieses Kontos. Das Modell weiß das und wiederholt sie nicht.
                </p>
              </div>

              <div className="v-knoepfe">
                <button type="submit" className="btn primary" disabled={busy || !name.trim()}>
                  {busy ? "Speichert …" : "Speichern"}
                </button>
                <div className="spacer" />
                <button type="button" className="btn quiet" onClick={() => void aktivSchalten()}>
                  {account.active ? "Pausieren" : "Wieder aktivieren"}
                </button>
                <button type="button" className="btn danger" onClick={() => void entfernen()}>Entfernen</button>
              </div>
            </form>
          )}
        </div>
      )}
    </div>
  );
}
